import React, { Component } from "react";
import {
  Row, Col, Card, Button, Spin, Tag, Empty,
  Tabs, Input, notification, Progress, Divider,
  Modal, Form, Select, Radio,
} from "antd";
import {
  HomeOutlined, ThunderboltOutlined, ToolOutlined,
  SmileOutlined, DollarOutlined, FileTextOutlined,
  SendOutlined, RobotOutlined, PlusOutlined,
  CheckCircleOutlined, ReloadOutlined,
} from "@ant-design/icons";
import { add, update, list, deleteRecord, noAuthGetInfo, noAuthgetDetails } from "../api/Apis";
import Advertisement from "./Advertisement";
import Amplify, { Storage } from "aws-amplify";
import AwsConfig from "../config/AwsConfig";
import ChecklistForm from "./ChecklistForm";

Amplify.configure(AwsConfig);

const { TabPane } = Tabs;
const { TextArea } = Input;

// ── Brand tokens ───────────────────────────────────────────────────────────────
const NAVY  = "#1A2280";
const GOLD  = "#D4921A";
const LIGHT = "#F7F9FC";
const BORDER = "#E2E8F0";
const MUTED = "#718096";
const WHITE = "#FFFFFF";

// ── AI proxy — routes through Netlify function to avoid browser CORS block ────
const AI_PROXY = "https://ai.myhomebuddha.com/.netlify/functions/claude";
const AI_MODEL = "claude-haiku-4-5-20251001";

// ── Section config ────────────────────────────────────────────────────────────
const SECTIONS = [
  {
    key: "home_health",
    aiKeys: ["health","watersource","sewer","electric","concerns"],
    label: "Home Health & Safety",
    icon: <HomeOutlined />,
    emoji: "🏥",
    color: "#E53E3E",
    points: [
      "Water source, quality & treatment systems",
      "Sewer / septic system condition",
      "Smoke & CO2 detectors — placement & age",
      "Indoor air quality & ventilation",
      "Electrical panel safety & capacity",
      "Fire risks, radon systems & lead paint",
      "Foundation moisture & structural concerns",
      "Trees, hazards & site safety",
    ],
    subsections: [
      { id:"watersource", emoji:"🚰", label:"Water Source & Quality",      hint:"Well, public water, filtration, softener, water test results" },
      { id:"sewer",       emoji:"🪣", label:"Sewer / Septic",              hint:"Sewer cleanout, septic tank, distribution box, leach field" },
      { id:"smoke_co",    emoji:"🔔", label:"Smoke & CO2 Detectors",       hint:"Location, brand, install date" },
      { id:"air_quality", emoji:"💨", label:"Air Quality & Ventilation",   hint:"HRV/ERV, bathroom fans, crawl space vents, mold concerns" },
      { id:"electric",    emoji:"⚡", label:"Electrical Panel",            hint:"Breakers, capacity, age, any concerns" },
      { id:"radon",       emoji:"☢️", label:"Radon & Hazardous Materials",  hint:"Mitigation system, asbestos, lead paint disclosures" },
      { id:"concerns",    emoji:"⚠️", label:"Areas of Concern",            hint:"Damage, moisture, staining, cracking, safety issues" },
    ],
  },
  {
    key: "energy_efficiency",
    aiKeys: ["energy","attic","windows"],
    label: "Energy Efficiency & Utility Savings",
    icon: <ThunderboltOutlined />,
    emoji: "⚡",
    color: "#D69E2E",
    points: [
      "Air leak areas & draft sources",
      "Insulation & attic effectiveness",
      "Windows & doors — seals, efficiency",
      "Electrical panel, lighting & smart upgrades",
      "Solar panels & renewable options",
      "Utility bill analysis & savings opportunities",
    ],
    subsections: [
      { id:"attic",       emoji:"🔍", label:"Attic & Insulation",         hint:"Insulation depth/type, ventilation, air sealing" },
      { id:"windows",     emoji:"🪟", label:"Windows & Doors",            hint:"Frame condition, glass type, weatherstripping, drafts" },
      { id:"air_sealing", emoji:"🌬️", label:"Air Sealing & Drafts",       hint:"Penetrations, rim joists, attic hatches, outlets" },
      { id:"solar",       emoji:"☀️", label:"Solar & Renewables",         hint:"Panels, inverter, battery storage, EV charger" },
      { id:"utility",     emoji:"📊", label:"Utility Bills & Meters",     hint:"Electric, gas, water usage — attach recent bills" },
    ],
  },
  {
    key: "maintenance_longevity",
    aiKeys: ["maintenance","roof","hvac","hwheater","plumbing","foundation","appliances"],
    label: "Maintenance, Repair & Longevity",
    icon: <ToolOutlined />,
    emoji: "🔧",
    color: "#2B6CB0",
    points: [
      "Roof condition, age & drainage",
      "Furnace / heating system performance",
      "Hot water heater — age & efficiency",
      "Plumbing systems & piping condition",
      "Foundation & basement health",
      "Appliances — age, condition & replacement timeline",
      "Other mechanical systems",
    ],
    subsections: [
      { id:"roof",        emoji:"🏠", label:"Roof",                       hint:"Shingles, gutters, chimney, skylights, flashing, age" },
      { id:"hvac",        emoji:"🌡️",  label:"Heating / HVAC",            hint:"Furnace, boiler, AC, heat pump — label with age & model" },
      { id:"hwheater",    emoji:"💧", label:"Water Heater",               hint:"Unit label — model, serial, install date, any rust" },
      { id:"plumbing",    emoji:"🔧", label:"Plumbing & Pipes",           hint:"Under sinks, main shutoff, pipe condition, water meter" },
      { id:"foundation",  emoji:"🏗️", label:"Foundation & Basement",      hint:"Walls, floor, cracks, moisture, sump pump" },
      { id:"appliances",  emoji:"🫙", label:"Appliances",                 hint:"Refrigerator, dishwasher, washer/dryer, range — model labels" },
      { id:"mechanical",  emoji:"⚙️", label:"Other Mechanical",           hint:"Generator, irrigation, central vacuum, other systems" },
    ],
  },
  {
    key: "other_spaces",
    aiKeys: ["other","garage","exterior"],
    label: "Other Spaces",
    icon: <HomeOutlined />,
    emoji: "🏡",
    color: "#5A67D8",
    points: [
      "Garage — door, floor, mechanicals",
      "Outbuildings & sheds",
      "Pool & spa condition",
      "Yard, grading & drainage",
      "Exterior & siding condition",
      "Landscaping & tree health",
    ],
    subsections: [
      { id:"garage",       emoji:"🚗", label:"Garage",                    hint:"Door, floor, walls, overhead storage, mechanicals" },
      { id:"exterior",     emoji:"🌿", label:"Exterior & Siding",         hint:"Siding, deck/porch, driveway, walkways, grading" },
      { id:"outbuildings", emoji:"🏚️", label:"Outbuildings & Sheds",      hint:"Sheds, barns, workshops, detached structures" },
      { id:"pool",         emoji:"🏊", label:"Pool, Spa & Water Features", hint:"Equipment, pump, filter, heater, liner condition" },
      { id:"yard",         emoji:"🌳", label:"Yard & Landscaping",        hint:"Trees near house, grading, drainage, irrigation" },
    ],
  },
  {
    key: "stress_reduction",
    aiKeys: ["stress"],
    label: "Stress Reduction & Simplicity",
    icon: <SmileOutlined />,
    emoji: "🧘",
    color: "#276749",
    points: [
      "Top 5 most important priorities right now",
      "What you don't need to worry about",
      "Low-effort, high-impact improvements",
      "Maintenance automation opportunities",
      "Home system organization & simplification",
    ],
  },
  {
    key: "value_wealth",
    aiKeys: ["value","kitchen","bathrooms"],
    label: "Value, Wealth & Future Proofing",
    icon: <DollarOutlined />,
    emoji: "💰",
    color: "#744210",
    points: [
      "Kitchen updates that pay off",
      "Bathroom improvements & ROI",
      "Cosmetic upgrades — paint, flooring, fixtures",
      "Aging items to plan for",
      "Pre-sale readiness & market positioning",
      "Energy & sustainability features buyers want",
    ],
    subsections: [
      { id:"kitchen",     emoji:"🍳", label:"Kitchen",                   hint:"Appliances, cabinets, counters, sink — age & condition" },
      { id:"bathrooms",   emoji:"🚿", label:"Bathrooms",                 hint:"Fixtures, tile, caulk, ventilation, vanity" },
      { id:"cosmetics",   emoji:"🎨", label:"Cosmetics & Finishes",      hint:"Paint, flooring, trim, ceiling condition" },
      { id:"upgrades",    emoji:"✨", label:"Upgrade Opportunities",     hint:"Areas with best before/after value potential" },
      { id:"aging_items", emoji:"📅", label:"Aging Items to Plan For",   hint:"Systems or finishes nearing end of life" },
    ],
  },
  {
    key: "notes_collaboration",
    aiKeys: ["notes"],
    label: "Notes & Collaboration",
    icon: <FileTextOutlined />,
    emoji: "📝",
    color: MUTED,
    points: ["Ideas", "Feedback", "Issues", "Tasks", "Summary"],
  },
];

const AI_KEY_MAP = {
  health:      "home_health",
  energy:      "energy_efficiency",
  maintenance: "maintenance_longevity",
  stress:      "stress_reduction",
  value:       "value_wealth",
};

class HomeBuddha extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeKey:       "overview",
      loading:         true,
      aiReport:        null,
      manualEntries:   {},
      chatMessages:    {},
      chatInput:       {},
      chatLoading:     {},
      openSubsections: {},
      subChatMessages: {},
      subChatInput:    {},
      subChatLoading:  {},
      subAnalyzing:    {},
      subAnalysis:     {},
      homes:           [],
      aiReports:       [],
      activeHomeId:    null,
      setupFor:        "self",
      clientEmail:     "",
      clientName:      "",
      referringAgent:  null,   // agent/lender who set up this portal
      agentChatOpen:   false,
      agentChatMessages: [],
      agentChatInput:  "",
      agentChatLoading: false,
      setupStep:       0,
      setupMode:       null,
      setupAddress:    "",
      setupPublicData: null,
      setupEdits:      {},
      setupLoading:    false,
      savedReports:    [],
      selectedReport:  null,
      reportsExpanded: false,
      myClients:       [],
      showInviteModal: false,
      inviteEmail:     "",
      inviteName:      "",
      inviteSending:   false,
      helpModal:       null,  // { sectionKey, sectionLabel } when open
      helpType:        "",    // "done" | "quote" | "quotes"
      helpNote:        "",
      helpSubmitting:  false,
    };
  }

  componentDidMount() {
    // Load all portal data first, then saved reports (needs homes state to be set)
    this.loadAll().then(() => this.loadSavedReports());
    this.loadMyClients();
  }

  // Load agent's existing clients (buyers/sellers with this agent as referrer)
  loadMyClients = async () => {
    try {
      const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
      const userId = ud.id || ud.userId;
      if (!userId || (ud.userProfile !== "AG" && ud.userProfile !== "L")) return;

      // Try fetching users referred by this agent using noAuthGetInfo
      // The portal backend accepts a referredBy query param
      const resp = await noAuthGetInfo(`getUsers?referredBy=${userId}`);
      if (resp?.success && Array.isArray(resp?.data) && resp.data.length > 0) {
        const clients = resp.data.filter(u =>
          u.userProfile === "B" || u.userProfile === "S" || u.userProfile === "HO"
        );
        console.log("[HomeBuddha] loadMyClients via referredBy:", clients.length);
        this.setState({ myClients: clients });
        return;
      }
    } catch(e) { console.warn("loadMyClients referredBy query:", e.message); }

    // Fallback: load from DynamoDB-stored coUsers on home records
    try {
      const homes2 = this.state.homes || [];
      const coUserIds = [];
      homes2.forEach(h => {
        (h.coUsers || []).forEach(cu => {
          if (cu.userId && !coUserIds.includes(cu.userId)) coUserIds.push(cu.userId);
        });
      });
      if (coUserIds.length > 0) {
        const fetched = await Promise.all(
          coUserIds.map(id => noAuthgetDetails("getUsers", id).catch(() => null))
        );
        const clients = fetched
          .filter(r => r?.success && r?.data)
          .map(r => r.data)
          .filter(u => u.userProfile === "B" || u.userProfile === "S" || u.userProfile === "HO");
        console.log("[HomeBuddha] loadMyClients via coUsers:", clients.length);
        if (clients.length > 0) this.setState({ myClients: clients });
      }
    } catch(e) { console.warn("loadMyClients coUsers fallback:", e.message); }
  };

  loadSavedReports = async () => {
    try {
      const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
      const userId = ud.id || ud.userId || ud.userid;
      if (!userId) return;

      // Source 1: reports saved directly under this user's userId
      const resp = await list("mybuddha", { userId });
      const directReports = resp?.success ? (resp.data || []).filter(r => r.address) : [];

      // Source 2: reports linked to home portal records (agent created for client)
      // Pull from both the fresh resp.data AND from this.state.homes (set by loadAll)
      const allHomeProfiles = [
        ...(resp?.data || []),
        ...(this.state.homes || []),
      ].filter((r, i, arr) =>
        (r.type === "home_profile" || r.action === "home_profile") &&
        arr.findIndex(x => x.id === r.id) === i // deduplicate
      );
      const homeProfiles = allHomeProfiles;
      const linkedReports = [];
      homeProfiles.forEach(home => {
        if (home.linkedReportData) {
          const lr = home.linkedReportData;
          // Synthesize a report-like object from the linked data
          linkedReports.push({
            id:         lr.id || home.id + "_linked",
            address:    lr.address || home.address || home.nickname || "",
            reportType: lr.reportType || "buyer",
            recordType: lr.reportType || "buyer",
            updatedAt:  lr.updatedAt || home.createdAt || 0,
            createdAt:  lr.updatedAt || home.createdAt || 0,
            isLinked:   true,   // flag so UI can show "shared by agent"
            agentId:    home.agentId || "",
          });
        }
      });

      // Merge and deduplicate by address + reportType
      const allReports = [...directReports, ...linkedReports];
      const seen = {};
      allReports.forEach(r => {
        const key = (r.address || "").toLowerCase().trim() + "|" + (r.reportType || r.recordType || "any");
        const ts  = r.updatedAt || r.createdAt || 0;
        if (!seen[key] || ts > (seen[key].updatedAt || 0)) seen[key] = r;
      });
      const reports = Object.values(seen).sort((a, b) =>
        (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0)
      );

      console.log("[HomeBuddha] loadSavedReports:", allReports.length, "total →", reports.length, "unique (", linkedReports.length, "linked)");
      this.setState({ savedReports: reports });
    } catch (err) {
      console.error("loadSavedReports:", err);
    }
  };

  fetchPublicRecords = async (address) => {
    this.setState({ setupLoading: true, setupPublicData: null });
    try {
      const resp = await fetch("https://ai.myhomebuddha.com/.netlify/functions/property-data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ address }),
      });
      let publicData = { address };
      if (resp.ok) {
        const data = await resp.json();
        const s = data.subject || {};
        const p = data.propData || {};
        const avmVal = data.avm ? String(data.avm.price || data.avm.value || "") : "";
        publicData = {
          address,
          yearBuilt:      String(s.yearBuilt      || p.yearBuilt      || ""),
          squareFeet:     String(s.sqft            || p.squareFootage  || p.livingArea || ""),
          bedrooms:       String(s.beds            || p.bedrooms       || ""),
          bathrooms:      String(s.baths           || p.bathrooms      || ""),
          lotSize:        String(p.lotSize         || p.lot_size       || ""),
          propertyType:   String(s.propertyType    || p.propertyType   || p.type || ""),
          stories:        String(p.stories         || ""),
          garageSpaces:   String(p.garageSpaces    || p.garage         || ""),
          heatingType:    String(p.heating         || p.heatingType    || ""),
          coolingType:    String(p.cooling         || p.coolingType    || ""),
          roofType:       String(p.roofType        || p.roof           || ""),
          foundationType: String(p.foundationType  || p.foundation     || ""),
          lastSoldYear:   p.lastSaleDate ? new Date(p.lastSaleDate).getFullYear().toString() : "",
          estimatedValue: avmVal,
          county:         String(p.county          || ""),
          state:          String(p.state           || p.stateCode      || ""),
          zipCode:        String(p.zipCode         || p.zip            || p.postalCode || ""),
        };
        if (!data.found || !data.subject) {
          notification.info({ message: "Partial property data", description: "Some fields couldn't be found automatically — please fill them in.", duration: 4 });
        }
      }
      this.setState({ setupPublicData: publicData, setupLoading: false, setupEdits: {}, setupStep: 2 });
    } catch (err) {
      console.error("fetchPublicRecords:", err);
      this.setState({ setupPublicData: { address }, setupLoading: false, setupEdits: {}, setupStep: 2 });
      notification.info({ message: "Couldn't auto-fill records", description: "Please enter your home details below.", duration: 3 });
    }
  };

  saveAndAnalyze = async () => {
    const { setupPublicData, setupEdits, setupAddress, selectedReport } = this.state;
    const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const userId = ud.id || ud.userId || ud.userid;
    if (!userId) return;
    const homeDetails = { ...(setupPublicData || {}), ...setupEdits };
    const address = homeDetails.address || setupAddress || selectedReport?.address || "";
    this.setState({ setupLoading: true });
    const { setupFor, clientEmail, clientName } = this.state;
    try {
      const coUsers = setupFor === "client" && clientEmail ? [{ email: clientEmail, name: clientName || clientEmail.split("@")[0], role: "owner", addedAt: Date.now() }] : [];
      // For client portals: look up client's userId by email
      let targetUserId = userId;
      let clientUserId = null;
      if (setupFor === "client" && clientEmail) {
        // Try multiple lookup methods
        try {
          // Method 1: checkUser by email (username lookup)
          const cl = await noAuthGetInfo("checkUser/" + encodeURIComponent(clientEmail));
          if (cl?.success && cl?.data?.[0]?.id) {
            clientUserId = cl.data[0].id;
            console.log("[HomeBuddha] client found via checkUser:", clientUserId);
          }
        } catch(e) { console.warn("checkUser lookup:", e.message); }

        if (!clientUserId) {
          try {
            // Method 2: search users by email
            const cl2 = await noAuthGetInfo("getUsers?email=" + encodeURIComponent(clientEmail));
            if (cl2?.success && cl2?.data?.[0]?.id) {
              clientUserId = cl2.data[0].id;
              console.log("[HomeBuddha] client found via email search:", clientUserId);
            }
          } catch(e) { console.warn("email search lookup:", e.message); }
        }

        if (clientUserId) {
          targetUserId = clientUserId;
        } else {
          console.warn("[HomeBuddha] client not found — saving under agent, client will see via coUsers lookup");
        }
      }
      const homeRecord = await add("mybuddha", {
        entityData: {
          userId: targetUserId,
          agentId: setupFor === "client" ? userId : null,
          type: "home_profile", action: "home_profile", address,
          nickname: address.split(",")[0] || (setupFor === "client" ? (clientName + "\'s Home") : "My Home"),
          homeDetails, coUsers,
          setupSource: selectedReport ? "saved_report" : "manual",
          linkedReportId: selectedReport?.id || null,
          linkedReportData: selectedReport ? { id: selectedReport.id, address: selectedReport.address || address, reportType: selectedReport.reportType || "buyer", updatedAt: selectedReport.updatedAt || Date.now() } : null,
          createdBy: userId, createdAt: Date.now(),
        },
      });
      if (setupFor === "client" && clientEmail) {
        try {
          const ud2 = JSON.parse(localStorage.getItem("userDetails") || "{}");
          const agName2 = `${ud2.firstName || ""} ${ud2.lastName || ""}`.trim();
          const agBrand2 = JSON.parse(localStorage.getItem("mhb_agent_brand") || "{}");
          // Use portal-created template when a report is attached, standalone invite otherwise
          const hasReport = !!selectedReport;
          const invResp2 = await fetch("https://ai.myhomebuddha.com/.netlify/functions/mhb-invite", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              toEmail:      clientEmail,
              toName:       clientName || clientEmail.split("@")[0],
              fromName:     agName2,
              fromEmail:    ud2.email || "",
              agentId:      ud2.id || ud2.userId || "",
              agentPhone:   ud2.phone || ud2.phno || "",
              agentBrand:   agBrand2,
              address:      address,
              homeId:       homeRecord?.data?.id || "",
              // Use portal-created template when report is attached
              templateId:   hasReport
                ? "d-5129af67a82e4e43bade36236d5340dd"
                : null, // null = use default SENDGRID_HOMEOWNER_INVITE_TEMPLATE_ID
              reports:      hasReport && selectedReport ? [{
                reportType: (selectedReport.reportType || selectedReport.recordType || "AI Home Analysis")
                  .replace(/_/g, " ").replace(/\w/g, c => c.toUpperCase()),
                date: selectedReport.updatedAt
                  ? new Date(selectedReport.updatedAt).toLocaleDateString()
                  : new Date().toLocaleDateString(),
              }] : [],
            }),
          });
          await invResp2.json();
          notification.success({ message: `Invite sent to ${clientEmail}` });
        } catch (inviteErr) {
          console.warn("Invite send failed:", inviteErr);
          notification.info({ message: "Home saved — invite email will be sent shortly" });
        }
      }
      notification.success({ message: "Home profile saved! Opening your portal..." });
      await this.loadAll();
      await this.loadSavedReports();
      if (this.props.onCloseSetupModal) this.props.onCloseSetupModal();
      this.setState({ setupStep: 0, setupMode: null, setupAddress: "", setupPublicData: null, setupEdits: {}, selectedReport: null, setupLoading: false, setupFor: "self", clientEmail: "", clientName: "", activeKey: "home_health" });
    } catch (err) {
      console.error("saveAndAnalyze:", err);
      notification.error({ message: "Error saving — please try again" });
      this.setState({ setupLoading: false });
    }
  };

  loadAll = async () => {
    // Returns promise so componentDidMount can chain loadSavedReports
    try {
      const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
      const userId = ud.id || ud.userId || ud.userid;
      const userEmail = ud.email || "";
      if (!userId) { this.setState({ loading: false }); return; }

      // Primary query: records owned by this user
      const resp = await list("mybuddha", { userId });
      if (!resp?.success || !Array.isArray(resp.data)) { this.setState({ loading: false }); return; }

      // Secondary query: find records an agent created FOR this client
      // Query by the referring agent's userId and filter for coUser match
      let coUserRecords = [];

      // Get refId — try localStorage first, then fetch fresh from DB
      let refId = ud.referedById || ud.referredBy || ud.invitedBy || ud.referredByAgent;
      if (!refId && userId) {
        try {
          const freshUser = await noAuthgetDetails("getUsers", userId);
          if (freshUser?.success && freshUser?.data) {
            const fu = freshUser.data;
            refId = fu.referedById || fu.referredBy || fu.invitedBy || fu.referredByAgent;
            if (refId) console.log("[HomeBuddha] got refId from fresh DB fetch:", refId);
          }
        } catch(e) { console.warn("fresh user fetch for refId:", e.message); }
      }
      console.log("[HomeBuddha] secondary query refId:", refId, "userEmail:", userEmail);

      if (refId && userEmail) {
        try {
          const agentResp = await list("mybuddha", { userId: refId });
          if (agentResp?.success && Array.isArray(agentResp.data)) {
            // Filter to records where this client's email is in coUsers
            const clientEmail = userEmail.toLowerCase();
            coUserRecords = agentResp.data.filter(r => {
              if (!r.coUsers || !Array.isArray(r.coUsers)) return false;
              return r.coUsers.some(cu =>
                cu.email?.toLowerCase() === clientEmail
              );
            });
            console.log("[HomeBuddha] agent records scanned:", agentResp.data.length, "→ coUser matches:", coUserRecords.length);
          }
        } catch(e) { console.warn("agent coUser query:", e.message); }
      }

      // Also check localStorage for fresh refId in case ud was stale
      if (coUserRecords.length === 0 && userEmail) {
        try {
          const freshUd = JSON.parse(localStorage.getItem("userDetails") || "{}");
          const freshRefId = freshUd.referedById || freshUd.referredBy || freshUd.invitedBy || freshUd.referredByAgent;
          if (freshRefId && freshRefId !== refId) {
            const freshResp = await list("mybuddha", { userId: freshRefId });
            if (freshResp?.success && Array.isArray(freshResp.data)) {
              const clientEmail = userEmail.toLowerCase();
              const freshMatches = freshResp.data.filter(r =>
                Array.isArray(r.coUsers) && r.coUsers.some(cu => cu.email?.toLowerCase() === clientEmail)
              );
              coUserRecords = [...coUserRecords, ...freshMatches];
              console.log("[HomeBuddha] fresh refId scan found:", freshMatches.length, "matches");
            }
          }
        } catch(e) { console.warn("fresh refId scan:", e.message); }
      }

      // Merge — deduplicate by id, prefer records owned by this user
      const seenIds = new Set(resp.data.map(r => r.id));
      coUserRecords.forEach(r => {
        if (!seenIds.has(r.id)) {
          seenIds.add(r.id);
          resp.data.push(r);
        }
      });
      console.log("[HomeBuddha] loadAll total after coUser merge:", resp.data.length, "(coUser added:", coUserRecords.length, ")");
      let aiReport = null;
      const manualEntries = {};
      const portalHomes = [], aiReports = [];
      resp.data.forEach(entry => {
        const sk = entry.sectionKey || entry.section || "";
        if (entry.address) {
          if (sk === "home_setup" || entry.type === "home_profile" || entry.action === "home_profile") {
            portalHomes.push(entry);
            if (!aiReport || (entry.updatedAt||0) > (aiReport.updatedAt||0)) aiReport = entry;
          } else { aiReports.push(entry); }
          return;
        }
        if (!manualEntries[sk]) manualEntries[sk] = [];
        manualEntries[sk].push({ id: entry.id, description: entry.description, notes: entry.notes, brand: entry.brand, model: entry.model, year: entry.year, rating: entry.rating, analysisResult: entry.analysisResult || "", subsectionId: entry.subsectionId, photos: (entry.photos || []).map((key, i) => ({ uid: `-${i+1}`, name: key || `photo${i+1}`, status: "done", url: (entry.photoUrls || [])[i] })) });
      });
      if (!aiReport && aiReports.length > 0) aiReport = aiReports.sort((a,b) => (b.updatedAt||b.createdAt||0)-(a.updatedAt||a.createdAt||0))[0];
      const allHomes = portalHomes.length > 0 ? portalHomes : aiReports.slice(0,1);
      const activeHomeId = this.state.activeHomeId || (allHomes.length > 0 ? allHomes[0].id : null);
      // Load referring agent/lender for branding
      // Priority: 1) coUsers on home record, 2) user's referedById from admin assignment
      let referringAgent = null;
      const ud2 = JSON.parse(localStorage.getItem("userDetails") || "{}");

      // Source 1: coUsers on the active home record
      const activeHome2 = portalHomes.length > 0 ? portalHomes.find(h => h.id === activeHomeId) || portalHomes[0] : null;
      if (activeHome2 && activeHome2.coUsers) {
        const agentUser = activeHome2.coUsers.find(u => u.role === "agent" || u.role === "lender");
        if (agentUser && agentUser.agentId) {
          try {
            // Try getUsers first (portal backend), fall back to getAgents (AI site)
            let agentResp = await noAuthgetDetails("getUsers", agentUser.agentId).catch(() => null);
            if (agentResp?.success && agentResp?.data) {
              referringAgent = agentResp.data;
            } else {
              const ar2 = await list("getAgents", { id: agentUser.agentId }).catch(() => null);
              if (ar2?.success && ar2?.data?.length > 0) referringAgent = ar2.data[0];
            }
          } catch(e) { console.warn("coUsers agent load failed:", e.message); }
        }
        if (!referringAgent && agentUser?.email) {
          referringAgent = { email: agentUser.email, firstName: agentUser.name || "Your Agent", lastName: "", phone: agentUser.phone || "" };
        }
      }

      // Source 2: user's referedById from admin assignment
      // Fetch fresh user record since localStorage may be stale
      if (!referringAgent) {
        const userId2 = ud2.id || ud2.userId || ud2.userid;
        let freshRefId = ud2.referedById || ud2.invitedBy || ud2.referredByAgent || ud2.referredBy;

        // Try fetching fresh user details from DB if no refId in cache
        if (!freshRefId && userId2) {
          try {
            const freshUser = await noAuthgetDetails("getAgents", userId2);
            if (freshUser?.success && freshUser?.data) {
              const u = freshUser.data;
              freshRefId = u.referedById || u.invitedBy || u.referredByAgent || u.referredBy;
              console.log("[HomeBuddha] fresh user refedById:", freshRefId);
            }
          } catch(e) { console.warn("fresh user fetch failed:", e.message); }
        }

        if (freshRefId && String(freshRefId).length > 10) {
          try {
            // Try getUsers first (portal backend)
            let agentResp2 = await noAuthgetDetails("getUsers", freshRefId).catch(() => null);
            if (agentResp2?.success && agentResp2?.data) {
              referringAgent = agentResp2.data;
              console.log("[HomeBuddha] agent found via getUsers/referedById:", referringAgent.firstName);
            } else {
              const ar3 = await list("getAgents", { id: freshRefId }).catch(() => null);
              if (ar3?.success && ar3?.data?.length > 0) {
                referringAgent = ar3.data[0];
                console.log("[HomeBuddha] agent found via getAgents/referedById:", referringAgent.firstName);
              }
            }
          } catch(e) { console.warn("referedById agent load failed:", e.message); }
        }
      }

      console.log("[HomeBuddha] referringAgent:", referringAgent?.firstName, referringAgent?.email || "none");
      this.setState({ loading: false, aiReport, manualEntries, homes: portalHomes, aiReports, activeHomeId, referringAgent });
    } catch (err) {
      console.error("HomeBuddha loadAll:", err);
      this.setState({ loading: false });
    }
  };

  handleSectionSubmit = async (values, recordId) => {
    try {
      const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
      const userId = ud.id || ud.userId;
      if (!userId) { notification.error({ message: "Please log in to save" }); return; }
      let uploadedPhotos = [], photoUrls = [];
      if (values.photos?.length) {
        const uploads = await Promise.all(values.photos.map(async file => {
          if (file.originFileObj) {
            const key = `${userId}_${Date.now()}_${file.name}`;
            const result = await Storage.put(key, file.originFileObj);
            const k = result.key || result;
            return { key: k, url: `https://${process.env.REACT_APP_S3_BUCKET}.s3.amazonaws.com/public/${k}` };
          }
          const match = file.url?.match(/public\/(.*)$/);
          return { key: match ? match[1] : file.name, url: file.url };
        }));
        uploadedPhotos = uploads.map(r => r.key);
        photoUrls = uploads.map(r => r.url);
      }
      const payload = { entityData: { userId, sectionKey: values.sectionKey || this.state.activeKey, subsectionId: values.subsectionId || null, description: values.description, notes: values.notes, brand: values.brand, model: values.model, year: values.year, rating: values.rating, analysisResult: values.analysisResult || "", photos: uploadedPhotos, photoUrls } };
      if (recordId) { await update("mybuddha", recordId, payload); notification.success({ message: "Updated" }); }
      else { await add("mybuddha", payload); notification.success({ message: "Saved" }); }
      await this.loadAll();
    } catch (err) {
      console.error(err);
      notification.error({ message: "Error saving" });
    }
  };

  handleSectionDelete = async (recordId) => {
    try {
      await deleteRecord("mybuddha", recordId);
      notification.success({ message: "Deleted" });
      await this.loadAll();
    } catch (err) { notification.error({ message: "Error deleting" }); }
  };

  // ── Analyze subsection — routes through Netlify proxy (fixes CORS) ───────────
  analyzeSubsection = async (sectionKey, sub) => {
    const subKey = sectionKey + ":" + sub.id;
    const section = SECTIONS.find(s => s.key === sectionKey);
    const { manualEntries, aiReport } = this.state;
    const sectionEntries = manualEntries[sectionKey] || [];
    const aiData = this.getAIDataForSection(sectionKey);
    const address = aiReport?.address || "this home";
    this.setState(s => ({ subAnalyzing: { ...s.subAnalyzing, [subKey]: true }, subAnalysis: { ...s.subAnalysis, [subKey]: "" } }));
    try {
      const context = [
        "Property: " + address,
        "Section: " + (section ? section.label : ""),
        "Subsection: " + sub.label,
        aiData ? "AI Report data: " + JSON.stringify(aiData).slice(0, 600) : "",
        sectionEntries.length > 0 ? "User notes: " + sectionEntries.map(e => e.description || e.notes || "").filter(Boolean).join("; ") : "",
      ].filter(Boolean).join("\n");
      const response = await fetch(AI_PROXY, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: AI_MODEL,
          max_tokens: 800,
          messages: [{ role: "user", content: "You are My Home Buddha AI — a home health expert. Analyze the \"" + sub.label + "\" subsection for " + address + ".\n\n" + context + "\n\nProvide:\n1. Current status assessment\n2. Key risks or concerns\n3. Recommended actions (prioritized)\n4. Estimated cost range if applicable\n\nBe specific and practical. 3-4 paragraphs max." }]
        }),
      });
      const data = await response.json();
      const result = data.content?.[0]?.text || "Analysis unavailable — please try again.";
      this.setState(s => ({ subAnalyzing: { ...s.subAnalyzing, [subKey]: false }, subAnalysis: { ...s.subAnalysis, [subKey]: result } }));
    } catch(err) {
      this.setState(s => ({ subAnalyzing: { ...s.subAnalyzing, [subKey]: false } }));
      notification.error({ message: "Analysis failed — please try again" });
    }
  };

  // ── Subsection chat — routes through Netlify proxy (fixes CORS) ─────────────
  sendSubChat = async (sectionKey, subId) => {
    const subKey = sectionKey + ":" + subId;
    const msg = (this.state.subChatInput[subKey] || "").trim();
    if (!msg) return;
    const section = SECTIONS.find(s => s.key === sectionKey);
    const sub = section?.subsections?.find(s => s.id === subId);
    const aiData = this.getAIDataForSection(sectionKey);
    const address = this.state.aiReport?.address || "this home";
    const systemPrompt = "You are My Home Buddha AI — a home health expert advising on the \"" + (sub ? sub.label : subId) + "\" component of " + address + ". " + (aiData ? "AI Report context: " + JSON.stringify(aiData).slice(0,500) : "") + " Be specific, practical, and concise.";
    const prev = this.state.subChatMessages[subKey] || [];
    const messages = [...prev, { role: "user", content: msg }];
    this.setState(s => ({ subChatMessages: { ...s.subChatMessages, [subKey]: messages }, subChatInput: { ...s.subChatInput, [subKey]: "" }, subChatLoading: { ...s.subChatLoading, [subKey]: true } }));
    try {
      const response = await fetch(AI_PROXY, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model: AI_MODEL, max_tokens: 600, system: systemPrompt, messages }),
      });
      const data = await response.json();
      const reply = data.content?.[0]?.text || "I couldn't respond — please try again.";
      this.setState(s => ({ subChatMessages: { ...s.subChatMessages, [subKey]: [...messages, { role: "assistant", content: reply }] }, subChatLoading: { ...s.subChatLoading, [subKey]: false } }));
    } catch(err) {
      this.setState(s => ({ subChatLoading: { ...s.subChatLoading, [subKey]: false } }));
    }
  };

  // ── Section chat — routes through Netlify proxy (fixes CORS) ────────────────
  sendChat = async (sectionKey) => {
    const userMsg = (this.state.chatInput[sectionKey] || "").trim();
    if (!userMsg) return;
    const section = SECTIONS.find(s => s.key === sectionKey);
    const aiReport = this.state.aiReport;
    const aiData   = this.getAIDataForSection(sectionKey);
    const context  = aiData ? `AI Analysis for ${section.label}: ${JSON.stringify(aiData).slice(0, 1000)}` : `Section: ${section.label}. No AI report available yet.`;
    const homeAddr = aiReport?.address || "this home";
    const systemPrompt = `You are My Home Buddha AI — a warm, knowledgeable home health advisor. \nYou're helping a homeowner with the "${section.label}" section of their home portal for ${homeAddr}.\n${context}\nGive practical, specific advice. Be encouraging but honest. Keep responses concise (2-4 paragraphs max).`;
    const prev = this.state.chatMessages[sectionKey] || [];
    const newMessages = [...prev, { role: "user", content: userMsg }];
    this.setState(s => ({ chatMessages: { ...s.chatMessages, [sectionKey]: newMessages }, chatInput: { ...s.chatInput, [sectionKey]: "" }, chatLoading: { ...s.chatLoading, [sectionKey]: true } }));
    try {
      const response = await fetch(AI_PROXY, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model: AI_MODEL, max_tokens: 1000, system: systemPrompt, messages: newMessages }),
      });
      const data = await response.json();
      const reply = data.content?.[0]?.text || "I'm having trouble responding right now — please try again.";
      this.setState(s => ({ chatMessages: { ...s.chatMessages, [sectionKey]: [...newMessages, { role: "assistant", content: reply }] }, chatLoading: { ...s.chatLoading, [sectionKey]: false } }));
    } catch (err) {
      this.setState(s => ({ chatLoading: { ...s.chatLoading, [sectionKey]: false } }));
      notification.error({ message: "Chat error — please try again" });
    }
  };

  // ── Ask My Agent chat — sends message stored in DynamoDB under home record ──
  sendAgentChat = async () => {
    const msg = (this.state.agentChatInput || "").trim();
    if (!msg) return;
    const { referringAgent, activeHomeId } = this.state;
    const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const userId = ud.id || ud.userId;
    const messages = [...this.state.agentChatMessages, { role: "user", content: msg, from: ud.firstName || "Client", timestamp: Date.now() }];
    this.setState({ agentChatMessages: messages, agentChatInput: "", agentChatLoading: true });
    try {
      // Save message to DynamoDB so agent can see it
      await add("mybuddha", {
        entityData: {
          userId, homeId: activeHomeId, type: "agent_chat_message",
          sectionKey: "agent_chat", message: msg,
          fromName: `${ud.firstName || ""} ${ud.lastName || ""}`.trim(),
          fromEmail: ud.email || "", timestamp: Date.now(),
          toEmail: referringAgent?.email || "",
        },
      });
      // Send email notification to agent
      if (referringAgent?.email) {
        await add("sendMail", {
          entityData: {
            to: referringAgent.email,
            from: ud.email || "noreply@myhomebuddha.com",
            subject: `💬 Home Portal Message from ${ud.firstName || "Your Client"}`,
            message: `${ud.firstName || "Your client"} sent you a message through their Home Buddha portal:

"${msg}"

Log in to respond: https://www.myhomebuddha.com`,
            isRegistred: true,
          },
        });
      }
      this.setState({ agentChatLoading: false });
      notification.success({ message: "Message sent to your agent!", duration: 3 });
    } catch(e) {
      console.error("sendAgentChat:", e);
      this.setState({ agentChatLoading: false });
      notification.error({ message: "Failed to send — please try again" });
    }
  };

  getAIDataForSection = (sectionKey) => {
    const { aiReport, aiReports } = this.state;
    const report = aiReport || (aiReports && aiReports[0]);
    if (!report) return null;
    const section = SECTIONS.find(s => s.key === sectionKey);
    if (!section) return null;
    const keys = [...(section.aiKeys || []), sectionKey];
    const findInObj = (obj) => { if (!obj || typeof obj !== "object") return null; for (const k of keys) { if (obj[k]) return obj[k]; } return null; };
    if (report.categoryData) { const found = findInObj(report.categoryData); if (found) return found; }
    if (report.reportData) {
      const rd = typeof report.reportData === "string" ? (() => { try { return JSON.parse(report.reportData); } catch(e) { return {}; } })() : report.reportData;
      if (rd.homeProfileResult) { const hpr = typeof rd.homeProfileResult === "string" ? (() => { try { return JSON.parse(rd.homeProfileResult); } catch(e) { return {}; } })() : rd.homeProfileResult; const found = findInObj(hpr); if (found) return found; }
      const found = findInObj(rd); if (found) return found;
    }
    if (report.homeProfileResult) { const hpr = typeof report.homeProfileResult === "string" ? (() => { try { return JSON.parse(report.homeProfileResult); } catch(e) { return {}; } })() : report.homeProfileResult; const found = findInObj(hpr); if (found) return found; }
    return null;
  };

  getSectionScore = (sectionKey) => {
    const aiData = this.getAIDataForSection(sectionKey);
    const manual = this.state.manualEntries[sectionKey] || [];
    if (!aiData && manual.length === 0) return null;
    return { score: aiData?.score || aiData?.rating || null, itemCount: manual.length, hasAI: !!aiData };
  };

  // ── Agent branding banner — shows at top of portal for referred clients ──────
  // ── Standalone homeowner invite modal ─────────────────────────────────────
  sendHomeownerInvite = async () => {
    const { inviteEmail, inviteName } = this.state;
    if (!inviteEmail.trim()) { notification.error({ message: "Please enter a client email" }); return; }
    const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const agentName = `${ud.firstName || ""} ${ud.lastName || ""}`.trim();
    this.setState({ inviteSending: true });
    try {
      // Send invite via portal's own backend (avoids CORS — portal can't call ai.myhomebuddha.com directly)
      const agBrand = JSON.parse(localStorage.getItem("mhb_agent_brand") || "{}");
      const invResult = await add("sendInviteEmail", {
        entityData: {
          toEmail:    inviteEmail.trim(),
          toName:     inviteName.trim() || inviteEmail.split("@")[0],
          fromName:   agentName,
          fromEmail:  ud.email || "",
          agentId:    ud.id || ud.userId || "",
          agentPhone: ud.phone || ud.phno || "",
          agentPhoto: agBrand.photo || "",
          agentCompany: agBrand.company || agBrand.brokerage || ud.newdata?.companyName || "",
          address:    "",
          homeId:     "",
          templateId: null,
          reports:    [],
        },
      });
      // If portal backend doesn't handle sendInviteEmail, fall back to direct Netlify call
      // (will only work if same-origin or CORS is configured)
      if (!invResult?.success) {
        console.warn("[HomeBuddha] portal invite failed, trying direct:", invResult);
      }
      notification.success({ message: `Invite sent to ${inviteEmail}!`, description: "They'll receive an email to create their account and home portal." });
      this.setState({ showInviteModal: false, inviteEmail: "", inviteName: "", inviteSending: false });
    } catch(e) {
      notification.error({ message: "Failed to send invite — please try again" });
      this.setState({ inviteSending: false });
    }
  };

  // ── Submit a vendor lead request from a section card ──────────────────────
  submitHelpRequest = async () => {
    const { helpModal, helpType, helpNote } = this.state;
    if (!helpType) { notification.error({ message: "Please select what type of help you need" }); return; }
    const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const activeHome = this.state.homes.find(h => h.id === this.state.activeHomeId) || this.state.homes[0];
    this.setState({ helpSubmitting: true });
    try {
      await add("createMissedLead", {
        entityData: {
          notificationType:   "vendor_request",
          leadType:           "vendor",
          serviceCategory:    helpModal.sectionLabel,
          sectionKey:         helpModal.sectionKey,
          helpType:           helpType,    // done | quote | quotes
          message:            helpNote || `Help needed with: ${helpModal.sectionLabel}`,
          address:            activeHome?.address || activeHome?.nickname || "",
          yearBuilt:          activeHome?.homeDetails?.yearBuilt || "",
          sqFootage:          activeHome?.homeDetails?.squareFeet || "",
          homeType:           activeHome?.homeDetails?.propertyType || "",
          requestorDetails: {
            name:   `${ud.firstName || ""} ${ud.lastName || ""}`.trim(),
            email:  ud.email || "",
            phone:  ud.phone || ud.phno || "",
          },
          isAssigned:     false,
          assignedStatus: "pending",
          createdAt:      Date.now(),
          userId:         ud.id || ud.userId || "",
        },
      });
      notification.success({
        message: "Request submitted!",
        description: "Our team will follow up with vendor options shortly.",
      });
      this.setState({ helpModal: null, helpType: "", helpNote: "", helpSubmitting: false });
    } catch(e) {
      notification.error({ message: "Failed to submit — please try again" });
      this.setState({ helpSubmitting: false });
    }
  };

  renderHelpModal() {
    const { helpModal, helpType, helpNote, helpSubmitting } = this.state;
    if (!helpModal) return null;
    const options = [
      { key: "done",   label: "🔧 Just Get It Done",       desc: "We find and coordinate a trusted vendor for you" },
      { key: "quote",  label: "📋 Get Me a Quote",          desc: "One vendor will contact you with a price" },
      { key: "quotes", label: "📊 Get Me Multiple Quotes",  desc: "2-3 vendors compete for your business" },
    ];
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(10,12,30,0.78)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
        onClick={() => this.setState({ helpModal: null, helpType: "", helpNote: "" })}>
        <div style={{ background: WHITE, borderRadius: "20px", maxWidth: "420px", width: "100%", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}
          onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div style={{ background: `linear-gradient(135deg, ${NAVY}, #2A3390)`, padding: "18px 22px" }}>
            <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: "3px" }}>Get Help With</div>
            <div style={{ color: WHITE, fontWeight: 800, fontSize: "17px" }}>{helpModal.sectionLabel}</div>
          </div>
          <div style={{ padding: "18px 22px" }}>
            <div style={{ fontSize: "12px", fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "10px" }}>What do you need?</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px" }}>
              {options.map(opt => (
                <div key={opt.key} onClick={() => this.setState({ helpType: opt.key })}
                  style={{ border: `2px solid ${helpType === opt.key ? NAVY : BORDER}`, borderRadius: "10px", padding: "12px 14px", cursor: "pointer", background: helpType === opt.key ? `${NAVY}08` : WHITE, transition: "all .15s" }}>
                  <div style={{ fontWeight: 700, fontSize: "13px", color: NAVY, marginBottom: "2px" }}>{opt.label}</div>
                  <div style={{ fontSize: "11px", color: MUTED }}>{opt.desc}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: "12px", fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "6px" }}>Add a Note <span style={{ fontWeight: 400, textTransform: "none" }}>(optional)</span></div>
            <Input.TextArea
              placeholder={`Describe what's needed with your ${helpModal.sectionLabel.toLowerCase()}...`}
              value={helpNote}
              onChange={e => this.setState({ helpNote: e.target.value })}
              rows={3}
              style={{ borderRadius: "8px", marginBottom: "14px" }} />
            <div style={{ display: "flex", gap: "10px" }}>
              <Button onClick={() => this.setState({ helpModal: null, helpType: "", helpNote: "" })}
                style={{ flex: 1, borderRadius: "50px" }}>Cancel</Button>
              <Button type="primary" loading={helpSubmitting} onClick={this.submitHelpRequest}
                style={{ flex: 2, background: NAVY, border: "none", borderRadius: "50px", fontWeight: 800 }}>
                Submit Request
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderInviteModal() {
    const { showInviteModal, inviteEmail, inviteName, inviteSending } = this.state;
    if (!showInviteModal) return null;
    const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const planRank = { free:0, basic:1, advanced:2, professional:3, team_leader:4 };
    const rank = planRank[ud.planTier || "free"] || 0;

    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(10,12,30,0.78)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
        onClick={() => this.setState({ showInviteModal: false })}>
        <div style={{ background: "white", borderRadius: "20px", maxWidth: "440px", width: "100%", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}
          onClick={e => e.stopPropagation()}>
          <div style={{ background: `linear-gradient(135deg, ${NAVY}, #2A3390)`, padding: "20px 24px" }}>
            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: "4px" }}>Invite a Homeowner</div>
            <div style={{ color: "white", fontWeight: 800, fontSize: "17px" }}>📨 Build Their Home Portal</div>
            <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "12px", marginTop: "4px", lineHeight: 1.5 }}>
              Your client creates a free account and their Home Buddha portal is permanently branded to you.
            </div>
          </div>
          <div style={{ padding: "20px 24px" }}>
            {rank < 2 ? (
              <div style={{ textAlign: "center", padding: "12px 0" }}>
                <div style={{ fontSize: "32px", marginBottom: "12px" }}>🔒</div>
                <div style={{ fontWeight: 800, color: NAVY, fontSize: "15px", marginBottom: "8px" }}>Advanced Plan Required</div>
                <div style={{ fontSize: "13px", color: MUTED, lineHeight: 1.6, marginBottom: "20px" }}>
                  Sending branded portal invites to clients requires the Advanced plan or higher. Upgrade to permanently link every client to your brand.
                </div>
                <a href="?tab=subscriptions"
                  style={{ display: "inline-block", background: NAVY, color: "white", textDecoration: "none", padding: "12px 28px", borderRadius: "50px", fontWeight: 800, fontSize: "13px" }}>
                  View Upgrade Options →
                </a>
              </div>
            ) : (
              <>
                <div style={{ fontSize: "12px", fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "10px" }}>Client Details</div>
                <Input placeholder="Client name" value={inviteName} onChange={e => this.setState({ inviteName: e.target.value })} style={{ marginBottom: "8px", borderRadius: "8px" }} />
                <Input placeholder="Client email address" value={inviteEmail} onChange={e => this.setState({ inviteEmail: e.target.value })} style={{ marginBottom: "16px", borderRadius: "8px" }} />
                <div style={{ background: "#F7F9FC", borderRadius: "10px", padding: "12px 14px", marginBottom: "16px", fontSize: "12px", color: MUTED, lineHeight: 1.6 }}>
                  <strong style={{ color: NAVY }}>What your client receives:</strong><br/>
                  ✓ Branded invitation email with your name &amp; photo<br/>
                  ✓ Free account at myhomebuddha.com<br/>
                  ✓ Home portal pre-set with your branding forever<br/>
                  ✓ Access to all AI home tools — free
                </div>
                <div style={{ display: "flex", gap: "10px" }}>
                  <Button onClick={() => this.setState({ showInviteModal: false })}
                    style={{ flex: 1, borderRadius: "50px" }}>Cancel</Button>
                  <Button type="primary" loading={inviteSending} onClick={this.sendHomeownerInvite}
                    style={{ flex: 2, background: GOLD, border: "none", borderRadius: "50px", color: "#2A1600", fontWeight: 800 }}>
                    📨 Send Invite
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  renderAgentBrandingBanner() {
    const { referringAgent } = this.state;
    if (!referringAgent) return null;
    const name = `${referringAgent.firstName || ""} ${referringAgent.lastName || ""}`.trim() || referringAgent.email || "Your Agent";
    const photo = referringAgent.profilePicture || referringAgent.photo || null;
    const company = referringAgent.companyName || referringAgent.title || "";
    const phone = referringAgent.phone || "";
    const email = referringAgent.email || "";
    const isLender = referringAgent.userProfile === "L";
    return (
      <div style={{ background: `linear-gradient(135deg, ${NAVY}, #2A3390)`, borderRadius: "14px", padding: "14px 18px", marginBottom: "16px", display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
        {photo ? (
          <img src={photo} alt={name} style={{ width: "52px", height: "52px", borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(212,146,26,0.6)", flexShrink: 0 }} />
        ) : (
          <div style={{ width: "52px", height: "52px", borderRadius: "50%", background: "rgba(212,146,26,0.2)", border: "2px solid rgba(212,146,26,0.4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px", flexShrink: 0 }}>
            {isLender ? "💰" : "🏠"}
          </div>
        )}
        <div style={{ flex: 1, minWidth: "140px" }}>
          <div style={{ fontSize: "10px", fontWeight: 700, color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: "2px" }}>
            {isLender ? "Your Lender" : "Your Agent"} · Powered by My Home Buddha
          </div>
          <div style={{ color: WHITE, fontWeight: 800, fontSize: "15px" }}>{name}</div>
          {company && <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "12px" }}>{company}</div>}
        </div>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", flexShrink: 0 }}>
          {phone && (
            <a href={`tel:${phone}`} style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "50px", padding: "7px 14px", color: WHITE, fontSize: "12px", fontWeight: 700, textDecoration: "none", display: "flex", alignItems: "center", gap: "5px" }}>
              📞 {phone}
            </a>
          )}
          {email && (
            <a href={`mailto:${email}`} style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "50px", padding: "7px 14px", color: WHITE, fontSize: "12px", fontWeight: 700, textDecoration: "none" }}>
              ✉️ Email
            </a>
          )}
          <button onClick={() => this.setState({ agentChatOpen: true })}
            style={{ background: GOLD, border: "none", borderRadius: "50px", padding: "7px 14px", color: "#2A1600", fontSize: "12px", fontWeight: 800, cursor: "pointer" }}>
            💬 Ask {isLender ? "Lender" : "Agent"}
          </button>
        </div>
      </div>
    );
  }

  // ── Ask My Agent chat panel ──────────────────────────────────────────────────
  renderAgentChatPanel() {
    const { referringAgent, agentChatOpen, agentChatMessages, agentChatInput, agentChatLoading } = this.state;
    if (!referringAgent || !agentChatOpen) return null;
    const name = `${referringAgent.firstName || ""} ${referringAgent.lastName || ""}`.trim() || "Your Agent";
    const isLender = referringAgent.userProfile === "L";
    return (
      <div style={{ position: "fixed", bottom: "20px", right: "20px", width: "340px", maxHeight: "480px", background: WHITE, borderRadius: "16px", boxShadow: "0 8px 40px rgba(0,0,0,0.2)", border: `1px solid ${BORDER}`, display: "flex", flexDirection: "column", zIndex: 1000 }}>
        {/* Header */}
        <div style={{ background: `linear-gradient(135deg, ${NAVY}, #2A3390)`, borderRadius: "16px 16px 0 0", padding: "12px 16px", display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{ flex: 1 }}>
            <div style={{ color: WHITE, fontWeight: 800, fontSize: "13px" }}>💬 Ask {isLender ? "Your Lender" : "Your Agent"}</div>
            <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "11px" }}>{name} · via My Home Buddha</div>
          </div>
          <button onClick={() => this.setState({ agentChatOpen: false })}
            style={{ background: "rgba(255,255,255,0.15)", border: "none", color: WHITE, borderRadius: "50%", width: "28px", height: "28px", cursor: "pointer", fontSize: "14px" }}>✕</button>
        </div>
        {/* Messages */}
        <div style={{ flex: 1, overflowY: "auto", padding: "14px", background: LIGHT, maxHeight: "300px" }}>
          {agentChatMessages.length === 0 && (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ fontSize: "28px", marginBottom: "8px" }}>🏠</div>
              <div style={{ fontSize: "12px", color: MUTED, lineHeight: 1.6 }}>
                Send a message to {name} — they'll be notified by email and can respond directly.
              </div>
              <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "6px" }}>
                {[
                  "Can we schedule a walkthrough?",
                  "I have questions about the inspection report",
                  "What's the next step in the process?",
                ].map((q, i) => (
                  <button key={i} onClick={() => this.setState({ agentChatInput: q })}
                    style={{ textAlign: "left", background: WHITE, border: `1px solid ${BORDER}`, borderRadius: "8px", padding: "8px 10px", fontSize: "12px", color: NAVY, cursor: "pointer" }}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}
          {agentChatMessages.map((msg, i) => (
            <div key={i} style={{ marginBottom: "10px", display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
              <div style={{ maxWidth: "85%", padding: "9px 12px", borderRadius: msg.role === "user" ? "12px 12px 2px 12px" : "12px 12px 12px 2px", background: msg.role === "user" ? NAVY : WHITE, color: msg.role === "user" ? WHITE : "#1A202C", fontSize: "12px", lineHeight: 1.6, border: msg.role === "assistant" ? `1px solid ${BORDER}` : "none" }}>
                {msg.content}
              </div>
            </div>
          ))}
          {agentChatLoading && (
            <div style={{ display: "flex", gap: "4px", padding: "6px 10px" }}>
              {[0, 0.2, 0.4].map(d => <div key={d} style={{ width: "6px", height: "6px", background: MUTED, borderRadius: "50%", animation: `pulse 1s ${d}s infinite` }} />)}
            </div>
          )}
        </div>
        {/* Input */}
        <div style={{ padding: "10px 12px", borderTop: `1px solid ${BORDER}`, background: WHITE, borderRadius: "0 0 16px 16px" }}>
          <div style={{ fontSize: "11px", color: MUTED, marginBottom: "6px" }}>
            {name} will be notified by email
          </div>
          <div style={{ display: "flex", gap: "6px" }}>
            <Input value={agentChatInput} onChange={e => this.setState({ agentChatInput: e.target.value })}
              onPressEnter={this.sendAgentChat}
              placeholder={`Message ${name}...`}
              style={{ borderRadius: "8px", fontSize: "12px" }} />
            <Button type="primary" icon={<SendOutlined />} loading={agentChatLoading}
              onClick={this.sendAgentChat}
              style={{ background: NAVY, border: "none", borderRadius: "8px" }} />
          </div>
        </div>
      </div>
    );
  }

  // ── Reports saved banner — shows for each property with dropdown ─────────────
  renderReportsBanner() {
    const { savedReports, aiReport } = this.state;
    if (!savedReports || savedReports.length === 0) return null;
    // Filter to reports matching the active home address
    const activeAddr = (aiReport?.address || "").toLowerCase().trim();
    const matchingReports = activeAddr
      ? savedReports.filter(r => (r.address || "").toLowerCase().trim() === activeAddr)
      : savedReports;
    if (matchingReports.length === 0) return null;
    return (
      <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: "12px", marginBottom: "14px", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px 16px", cursor: "pointer", background: `${NAVY}06` }}
          onClick={() => this.setState(s => ({ reportsExpanded: !s.reportsExpanded }))}>
          <span style={{ fontSize: "18px" }}>📋</span>
          <div style={{ flex: 1 }}>
            <span style={{ fontWeight: 700, color: NAVY, fontSize: "13px" }}>
              {matchingReports.length} AI Report{matchingReports.length !== 1 ? "s" : ""} Saved
            </span>
            <span style={{ fontSize: "11px", color: MUTED, marginLeft: "8px" }}>
              Click to {this.state.reportsExpanded ? "hide" : "view"}
            </span>
          </div>
          <span style={{ color: MUTED, transition: "transform .2s", transform: this.state.reportsExpanded ? "rotate(180deg)" : "none" }}>⌄</span>
        </div>
        {this.state.reportsExpanded && (
          <div style={{ borderTop: `1px solid ${BORDER}` }}>
            {matchingReports.map((r, i) => (
              <div key={r.id || i} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 16px", borderBottom: i < matchingReports.length - 1 ? `1px solid ${BORDER}` : "none" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div style={{ fontWeight: 600, fontSize: "13px", color: "#1A202C" }}>
                      {(r.reportType || r.recordType || "Report").replace(/_/g, " ")}
                    </div>
                    {r.isLinked && (
                      <span style={{ fontSize: "10px", fontWeight: 700, color: GOLD, background: `${GOLD}15`, padding: "1px 7px", borderRadius: "50px", border: `1px solid ${GOLD}40` }}>
                        🤝 Agent
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: "11px", color: MUTED }}>
                    {r.updatedAt ? new Date(r.updatedAt).toLocaleDateString() : ""}
                    {r.address && r.address !== aiReport?.address ? ` · ${r.address}` : ""}
                  </div>
                </div>
                <a href={`https://ai.myhomebuddha.com?loadReport=${r.id}&mode=${r.reportType === 'seller' || r.recordType === 'seller' ? 'seller' : r.reportType === 'homeowner' || r.recordType === 'homeowner' ? 'homeowner' : 'buyer'}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: "11px", color: NAVY, fontWeight: 700, textDecoration: "none", background: `${NAVY}10`, borderRadius: "50px", padding: "4px 10px", flexShrink: 0 }}>
                  View →
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  renderOverview() {
    const { aiReport, manualEntries, homes, activeHomeId } = this.state;
    const totalManual = Object.values(manualEntries).reduce((a, b) => a + b.length, 0);
    const hasReport = !!aiReport;
    const activeHome = homes.find(h => h.id === activeHomeId) || null;
    return (
      <div>



      <div style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #2A3390 100%)`, borderRadius: "16px", padding: "28px 24px", marginBottom: "20px", boxShadow: "0 6px 24px rgba(26,34,128,0.18)" }}>
          <div style={{ fontSize: "32px", marginBottom: "8px" }}>🏡</div>
          <h2 style={{ color: WHITE, fontWeight: 800, fontSize: "22px", margin: "0 0 6px" }}>Your Home Buddha Portal</h2>
          <p style={{ color: "rgba(255,255,255,0.75)", fontSize: "13px", margin: "0 0 16px", lineHeight: 1.6 }}>Your personal AI home health dashboard. Powered by your AI site analysis and your own notes.</p>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <div style={{ background: "rgba(255,255,255,0.12)", borderRadius: "10px", padding: "12px 16px", textAlign: "center", minWidth: "90px" }}>
              <div style={{ color: GOLD, fontWeight: 800, fontSize: "22px" }}>{hasReport ? "✓" : "—"}</div>
              <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "11px", marginTop: "2px" }}>AI Report</div>
            </div>
            <div style={{ background: "rgba(255,255,255,0.12)", borderRadius: "10px", padding: "12px 16px", textAlign: "center", minWidth: "90px" }}>
              <div style={{ color: GOLD, fontWeight: 800, fontSize: "22px" }}>{totalManual}</div>
              <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "11px", marginTop: "2px" }}>Notes Saved</div>
            </div>
            <div style={{ background: "rgba(255,255,255,0.12)", borderRadius: "10px", padding: "12px 16px", textAlign: "center", minWidth: "90px" }}>
              <div style={{ color: GOLD, fontWeight: 800, fontSize: "22px" }}>6</div>
              <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "11px", marginTop: "2px" }}>Categories</div>
            </div>
            {aiReport?.address && (
              <div style={{ background: "rgba(255,255,255,0.12)", borderRadius: "10px", padding: "12px 16px", flex: 1, minWidth: "160px" }}>
                <div style={{ color: GOLD, fontWeight: 700, fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "3px" }}>Property</div>
                <div style={{ color: WHITE, fontSize: "13px", fontWeight: 600 }}>{aiReport.address}</div>
              </div>
            )}
          </div>
          {!hasReport && (
            <div style={{ marginTop: "16px", background: "rgba(212,146,26,0.2)", borderRadius: "10px", padding: "12px 16px" }}>
              <div style={{ color: GOLD, fontWeight: 700, fontSize: "13px", marginBottom: "4px" }}>🤖 No AI Report Yet</div>
              <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "12px", lineHeight: 1.5 }}>Run a Home Health Analysis on the AI site to populate this dashboard with personalized insights for your home.</div>
              <a href={"https://ai.myhomebuddha.com?autoLogin=1&userId=" + (JSON.parse(localStorage.getItem("userDetails") || "{}").id || "")} target="_blank" rel="noopener noreferrer"
                style={{ display: "inline-block", marginTop: "10px", background: GOLD, color: "#2A1600", borderRadius: "50px", padding: "7px 16px", fontWeight: 700, fontSize: "12px", textDecoration: "none" }}>
                Go to AI Site →
              </a>
            </div>
          )}

        </div>


        {this.renderReportsBanner()}

        {/* ── Sidebar ad slot ─────────────────────────────────────────── */}
        <div style={{ borderRadius: "12px", overflow: "hidden", marginTop: "12px", marginBottom: "4px" }}>
          <Advertisement otherAds={false} adType="Square" adWhereToShow="HomeBuddhaSidebar" adLimit="1" zipCode="" />
        </div>

        {activeHome?.coUsers?.length > 0 && (
          <div style={{ background: WHITE, borderRadius: "12px", border: `1px solid ${BORDER}`, padding: "14px 16px", marginBottom: "16px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "10px" }}>Home Portal Access</div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {activeHome.coUsers.map((u, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px", background: LIGHT, borderRadius: "50px", padding: "5px 12px 5px 6px", border: `1px solid ${BORDER}` }}>
                  <div style={{ width: "24px", height: "24px", borderRadius: "50%", background: NAVY, color: WHITE, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: 700 }}>{(u.name || "?")[0].toUpperCase()}</div>
                  <span style={{ fontSize: "12px", fontWeight: 600, color: "#1A202C" }}>{u.name}</span>
                  <span style={{ fontSize: "10px", color: MUTED, background: "#E2E8F0", borderRadius: "50px", padding: "1px 6px" }}>{u.role}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <Row gutter={[14, 14]}>
          {SECTIONS.map(section => {
            const manual = manualEntries[section.key] || [];
            const aiData = this.getAIDataForSection(section.key);
            return (
              <Col key={section.key} xs={24} sm={12} lg={8}>
                <Card style={{ borderRadius: "12px", border: `1px solid ${BORDER}` }} bodyStyle={{ padding: "16px" }}>
                  {/* Clickable section header */}
                  <div onClick={() => this.setState({ activeKey: section.key })} style={{ cursor: "pointer" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                      <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: `${section.color}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px" }}>{section.emoji}</div>
                      <div style={{ flex: 1 }}><div style={{ fontWeight: 700, fontSize: "13px", color: "#1A202C", lineHeight: 1.2 }}>{section.label}</div></div>
                    </div>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "10px" }}>
                      {aiData && <Tag color="blue" style={{ fontSize: "10px", borderRadius: "50px" }}>🤖 AI Data</Tag>}
                      {manual.length > 0 && <Tag color="green" style={{ fontSize: "10px", borderRadius: "50px" }}>{manual.length} note{manual.length !== 1 ? "s" : ""}</Tag>}
                      {!aiData && manual.length === 0 && <Tag style={{ fontSize: "10px", borderRadius: "50px", color: MUTED }}>Empty — click to add</Tag>}
                    </div>
                  </div>
                  {/* I need help button */}
                  <button
                    onClick={e => { e.stopPropagation(); this.setState({ helpModal: { sectionKey: section.key, sectionLabel: section.label }, helpType: "", helpNote: "" }); }}
                    style={{ width: "100%", background: `${section.color}12`, border: `1px solid ${section.color}30`, borderRadius: "8px", padding: "6px 10px", color: section.color, fontSize: "11px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", textAlign: "center" }}>
                    🛠 I Need Help With This
                  </button>
                </Card>
              </Col>
            );
          })}
        </Row>
      </div>
    );
  }

  renderSubsection(section, sub) {
    const subKey = section.key + ":" + sub.id;
    const isOpen = this.state.openSubsections[subKey] || false;
    const { subChatMessages, subChatInput, subChatLoading, subAnalyzing, subAnalysis, manualEntries } = this.state;
    const msgs        = subChatMessages[subKey] || [];
    const chatVal     = subChatInput[subKey]    || "";
    const isLoading   = subChatLoading[subKey]  || false;
    const isAnalyzing = subAnalyzing[subKey]    || false;
    const analysis    = subAnalysis[subKey]     || "";
    const manual      = manualEntries[section.key] || [];
    const hasData     = analysis || msgs.length > 0 || manual.length > 0;
    return (
      <div key={sub.id} style={{ border: `1px solid ${isOpen ? section.color + "40" : BORDER}`, borderRadius: "12px", marginBottom: "10px", overflow: "hidden", transition: "border-color .2s", boxShadow: isOpen ? `0 2px 12px ${section.color}15` : "none" }}>
        <div onClick={() => this.setState(s => ({ openSubsections: { ...s.openSubsections, [subKey]: !isOpen } }))}
          style={{ display: "flex", alignItems: "center", gap: "12px", padding: "14px 16px", cursor: "pointer", background: isOpen ? `${section.color}08` : WHITE, transition: "background .2s" }}>
          <span style={{ fontSize: "22px", flexShrink: 0 }}>{sub.emoji}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: NAVY, fontSize: "14px" }}>{sub.label}</div>
            <div style={{ fontSize: "11px", color: MUTED, marginTop: "1px" }}>{sub.hint}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {hasData && <span style={{ fontSize: "10px", background: `${section.color}20`, color: section.color, borderRadius: "50px", padding: "2px 8px", fontWeight: 700 }}>Has data</span>}
            <span style={{ color: MUTED, fontSize: "18px", transform: isOpen ? "rotate(180deg)" : "none", transition: "transform .2s" }}>⌄</span>
          </div>
        </div>
        {isOpen && (
          <div style={{ borderTop: `1px solid ${BORDER}` }}>
            <Row gutter={0}>
              <Col xs={24} lg={14} style={{ borderRight: `1px solid ${BORDER}`, padding: "16px" }}>
                {analysis && (
                  <div style={{ background: `${NAVY}06`, borderRadius: "10px", padding: "14px", marginBottom: "14px", border: `1px solid ${NAVY}15` }}>
                    <div style={{ fontSize: "11px", fontWeight: 700, color: GOLD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "8px" }}>🤖 AI Analysis</div>
                    <div style={{ fontSize: "13px", color: "#2D3748", lineHeight: 1.7, whiteSpace: "pre-line" }}>{analysis}</div>
                  </div>
                )}
                <div style={{ display: "flex", gap: "8px", marginBottom: "14px", flexWrap: "wrap" }}>
                  <Button type={analysis ? "default" : "primary"} loading={isAnalyzing} onClick={() => this.analyzeSubsection(section.key, sub)}
                    style={{ borderRadius: "50px", fontWeight: 700, background: analysis ? WHITE : NAVY, borderColor: analysis ? BORDER : NAVY, color: analysis ? NAVY : WHITE }}
                    icon={<RobotOutlined />}>
                    {analysis ? "Re-analyze" : "Analyze " + sub.label}
                  </Button>
                  <Button
                    onClick={() => this.setState({ helpModal: { sectionKey: section.key, sectionLabel: sub.label }, helpType: "", helpNote: "" })}
                    style={{ borderRadius: "50px", fontWeight: 700, background: `${section.color}12`, border: `1px solid ${section.color}40`, color: section.color }}
                    icon={<ToolOutlined />}>
                    I Need Help
                  </Button>
                </div>
                <ChecklistForm title={sub.label} points={[sub.hint]}
                  onSectionSubmit={(values, recordId) => { this.setState({ activeKey: section.key }); this.handleSectionSubmit({ ...values, sectionKey: section.key, subsectionId: sub.id }, recordId); }}
                  onSectionDelete={this.handleSectionDelete}
                  submittedData={manual.filter(e => e.subsectionId === sub.id)} />
              </Col>
              <Col xs={24} lg={10} style={{ padding: "0", display: "flex", flexDirection: "column" }}>
                <div style={{ background: `linear-gradient(135deg, ${NAVY}, #2A3390)`, padding: "12px 14px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <RobotOutlined style={{ color: GOLD, fontSize: "16px" }} />
                    <div>
                      <div style={{ color: WHITE, fontWeight: 700, fontSize: "12px" }}>{sub.label + " Advisor"}</div>
                      <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "10px" }}>Ask specific questions about this area</div>
                    </div>
                  </div>
                </div>
                <div style={{ flex: 1, overflowY: "auto", maxHeight: "280px", padding: "12px 14px", background: LIGHT }}>
                  {msgs.length === 0 && (
                    <div style={{ textAlign: "center", padding: "16px 0" }}>
                      <div style={{ fontSize: "20px", marginBottom: "6px" }}>{sub.emoji}</div>
                      <div style={{ fontSize: "11px", color: MUTED, lineHeight: 1.5 }}>{"Ask anything about " + sub.label.toLowerCase() + " for your home."}</div>
                      <div style={{ marginTop: "10px", display: "flex", flexDirection: "column", gap: "5px" }}>
                        {["What should I know about my " + sub.label.toLowerCase() + "?", "How do I maintain my " + sub.label.toLowerCase() + "?", "What are warning signs I should watch for?"].map((q, i) => (
                          <button key={i} onClick={() => this.setState(s => ({ subChatInput: { ...s.subChatInput, [subKey]: q } }), () => this.sendSubChat(section.key, sub.id))}
                            style={{ textAlign: "left", background: WHITE, border: `1px solid ${BORDER}`, borderRadius: "8px", padding: "7px 10px", fontSize: "11px", color: NAVY, cursor: "pointer", lineHeight: 1.4 }}>{q}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {msgs.map((msg, i) => (
                    <div key={i} style={{ marginBottom: "10px", display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
                      <div style={{ maxWidth: "88%", padding: "8px 11px", borderRadius: msg.role === "user" ? "12px 12px 2px 12px" : "12px 12px 12px 2px", background: msg.role === "user" ? NAVY : WHITE, color: msg.role === "user" ? WHITE : "#1A202C", fontSize: "11px", lineHeight: 1.6, border: msg.role === "assistant" ? `1px solid ${BORDER}` : "none" }}>{msg.content}</div>
                    </div>
                  ))}
                  {isLoading && <div style={{ display: "flex", gap: "4px", padding: "6px 10px" }}>{[0,.2,.4].map(d => <div key={d} style={{ width: "5px", height: "5px", background: MUTED, borderRadius: "50%", animation: `pulse 1s ${d}s infinite` }} />)}</div>}
                </div>
                <div style={{ padding: "10px 12px", borderTop: `1px solid ${BORDER}`, background: WHITE }}>
                  <div style={{ display: "flex", gap: "6px" }}>
                    <TextArea value={chatVal} onChange={e => this.setState(s => ({ subChatInput: { ...s.subChatInput, [subKey]: e.target.value } }))}
                      onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); this.sendSubChat(section.key, sub.id); } }}
                      placeholder={"Ask about " + sub.label.toLowerCase() + "..."} autoSize={{ minRows: 1, maxRows: 3 }} style={{ borderRadius: "8px", fontSize: "11px", resize: "none" }} />
                    <Button type="primary" icon={<SendOutlined />} loading={isLoading} onClick={() => this.sendSubChat(section.key, sub.id)} style={{ background: NAVY, border: "none", borderRadius: "8px", height: "auto", minHeight: "32px" }} />
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        )}
      </div>
    );
  }

  renderSection(section) {
    const { chatMessages, chatInput, chatLoading, aiReport } = this.state;
    const messages      = chatMessages[section.key] || [];
    const chatVal       = chatInput[section.key] || "";
    const isChatLoading = chatLoading[section.key] || false;
    const hasSubsections = section.subsections && section.subsections.length > 0;
    const starters = {
      home_health:           ["What are the biggest health risks in my home?", "How do I improve indoor air quality?", "What should I check for moisture damage?"],
      energy_efficiency:     ["How can I reduce my energy bills this winter?", "What's the best insulation upgrade for my budget?", "Should I get solar panels?"],
      maintenance_longevity: ["What maintenance should I do this season?", "How do I know when to replace my roof?", "What deferred maintenance is most urgent?"],
      other_spaces:          ["How should I winterize my outdoor spaces?", "What should I look for in my garage?", "How do I maintain my yard for home value?"],
      stress_reduction:      ["What should I focus on first?", "What can I safely ignore for now?", "How do I automate home maintenance reminders?"],
      value_wealth:          ["What improvements add the most value?", "How do I prepare my home for sale?", "What cosmetic updates have best ROI?"],
      notes_collaboration:   ["Summarize my home notes so far", "What follow-ups do I have outstanding?", "Help me organize my home tasks"],
    };
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px", padding: "16px 20px", background: `linear-gradient(135deg, ${NAVY}08, transparent)`, borderRadius: "12px", borderLeft: `4px solid ${section.color}` }}>
          <span style={{ fontSize: "28px" }}>{section.emoji}</span>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, color: NAVY, fontWeight: 800, fontSize: "17px" }}>{section.label}</h3>
            <div style={{ fontSize: "12px", color: MUTED, marginTop: "2px" }}>{hasSubsections ? `${section.subsections.length} areas · click any area below to expand` : ""}</div>
          </div>
          {!aiReport?.address && (
            <a href={"https://ai.myhomebuddha.com?autoLogin=1&userId=" + (JSON.parse(localStorage.getItem("userDetails") || "{}").id || "")} target="_blank" rel="noopener noreferrer"
              style={{ fontSize: "11px", color: NAVY, background: `${GOLD}20`, borderRadius: "50px", padding: "4px 12px", fontWeight: 700, textDecoration: "none", flexShrink: 0 }}>
              🤖 Run AI Analysis
            </a>
          )}
          {this.state.referringAgent && (
            <button onClick={() => this.setState({ agentChatOpen: true })}
              style={{ background: GOLD, border: "none", borderRadius: "50px", padding: "5px 12px", color: "#2A1600", fontSize: "11px", fontWeight: 800, cursor: "pointer", flexShrink: 0 }}>
              💬 Ask {this.state.referringAgent.userProfile === "L" || this.state.referringAgent.userType === "L" ? "Your Lender" : "Your Agent"}
            </button>
          )}
        </div>
        {/* ── Full-width section AI chat — above subsections ─────────────── */}
        <div style={{ marginBottom: "20px", border: `1px solid ${NAVY}25`, borderRadius: "12px", overflow: "hidden" }}>
          {/* Collapsed header — always visible */}
          <div
            onClick={() => this.setState(s => ({ [`chat_open_${section.key}`]: !s[`chat_open_${section.key}`] }))}
            style={{ display: "flex", alignItems: "center", gap: "10px", padding: "12px 16px", background: `linear-gradient(135deg, ${NAVY}, #2A3390)`, cursor: "pointer" }}>
            <RobotOutlined style={{ color: GOLD, fontSize: "16px" }} />
            <div style={{ flex: 1 }}>
              <span style={{ color: WHITE, fontWeight: 700, fontSize: "13px" }}>{section.label} Advisor</span>
              <span style={{ color: "rgba(255,255,255,0.55)", fontSize: "11px", marginLeft: "8px" }}>
                {messages.length > 0 ? `${messages.length} message${messages.length !== 1 ? "s" : ""}` : "Ask broad questions about this category"}
              </span>
            </div>
            <span style={{ color: "rgba(255,255,255,0.6)", fontSize: "16px", transform: this.state[`chat_open_${section.key}`] ? "rotate(180deg)" : "none", transition: "transform .2s" }}>⌄</span>
          </div>

          {/* Expanded chat body */}
          {this.state[`chat_open_${section.key}`] && (
            <div>
              <div style={{ maxHeight: "260px", overflowY: "auto", padding: "14px 16px", background: LIGHT }}>
                {messages.length === 0 && (
                  <div>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      {(starters[section.key] || []).map((q, i) => (
                        <button key={i} onClick={() => this.setState(s => ({ chatInput: { ...s.chatInput, [section.key]: q } }), () => this.sendChat(section.key))}
                          style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: "8px", padding: "8px 12px", fontSize: "12px", color: NAVY, cursor: "pointer", lineHeight: 1.4, fontWeight: 500 }}>{q}</button>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Section ad — shows below suggestions in right chat panel ── */}
                {messages.length === 0 && (
                  <div style={{ margin: "10px 8px", borderRadius: "10px", overflow: "hidden", opacity: 0.92 }}>
                    <Advertisement otherAds={false} adType="Square"
                      adWhereToShow={"HomeBuddha_" + section.key}
                      adLimit="1" zipCode="" />
                  </div>
                )}

                {messages.map((msg, i) => (
                  <div key={i} style={{ marginBottom: "10px", display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
                    <div style={{ maxWidth: "75%", padding: "10px 13px", borderRadius: msg.role === "user" ? "14px 14px 2px 14px" : "14px 14px 14px 2px", background: msg.role === "user" ? NAVY : WHITE, color: msg.role === "user" ? WHITE : "#1A202C", fontSize: "12px", lineHeight: 1.6, border: msg.role === "assistant" ? `1px solid ${BORDER}` : "none" }}>{msg.content}</div>
                  </div>
                ))}
                {isChatLoading && <div style={{ display: "flex", gap: "4px", padding: "8px 12px" }}>{[0,.2,.4].map(d => <div key={d} style={{ width: "6px", height: "6px", background: MUTED, borderRadius: "50%", animation: `pulse 1s ${d}s infinite` }} />)}</div>}
              </div>
              <div style={{ padding: "10px 14px", borderTop: `1px solid ${BORDER}`, background: WHITE }}>
                <div style={{ display: "flex", gap: "8px" }}>
                  <TextArea value={chatVal} onChange={e => this.setState(s => ({ chatInput: { ...s.chatInput, [section.key]: e.target.value } }))}
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); this.sendChat(section.key); } }}
                    placeholder="Ask about this section..." autoSize={{ minRows: 1, maxRows: 3 }} style={{ borderRadius: "8px", fontSize: "12px", resize: "none" }} />
                  <Button type="primary" icon={<SendOutlined />} loading={isChatLoading} onClick={() => this.sendChat(section.key)} style={{ background: NAVY, border: "none", borderRadius: "8px", height: "auto", minHeight: "36px" }} />
                </div>
                <div style={{ fontSize: "10px", color: MUTED, marginTop: "4px" }}>Enter to send · Shift+Enter for new line</div>
              </div>
            </div>
          )}
        </div>

        {/* ── Full-width subsections / checklist ────────────────────────── */}
        {hasSubsections ? <div>{section.subsections.map(sub => this.renderSubsection(section, sub))}</div> : (
          <ChecklistForm title={section.label} points={section.points} onSectionSubmit={this.handleSectionSubmit} onSectionDelete={this.handleSectionDelete} submittedData={this.state.manualEntries[section.key] || []} />
        )}

      </div>
    );
  }

  renderSetupModal() {
    const { setupStep, setupMode, setupAddress, setupPublicData, setupEdits, setupLoading, savedReports, selectedReport } = this.state;
    const isOpen = this.props.showSetupModal || false;
    const merged = { ...(setupPublicData || {}), ...setupEdits };
    const FIELDS = [
      { key: "yearBuilt", label: "Year Built" }, { key: "squareFeet", label: "Square Feet" },
      { key: "bedrooms", label: "Bedrooms" }, { key: "bathrooms", label: "Bathrooms" },
      { key: "lotSize", label: "Lot Size" }, { key: "propertyType", label: "Property Type" },
      { key: "stories", label: "Stories" }, { key: "garageSpaces", label: "Garage Spaces" },
      { key: "heatingType", label: "Heating Type" }, { key: "coolingType", label: "Cooling/AC Type" },
      { key: "roofType", label: "Roof Type" }, { key: "foundationType", label: "Foundation Type" },
      { key: "estimatedValue", label: "Estimated Value" },
    ];
    const steps = ["Choose Source", "Enter Address", "Confirm Details", "Analyze"];
    return (
      <Modal visible={isOpen}
        title={<div style={{ display: "flex", alignItems: "center", gap: "10px" }}><span style={{ fontSize: "20px" }}>🏡</span><span style={{ color: NAVY, fontWeight: 800 }}>Create a Home Profile</span></div>}
        onCancel={() => { if (this.props.onCloseSetupModal) this.props.onCloseSetupModal(); this.setState({ setupStep: 0, setupMode: null, setupAddress: "", setupPublicData: null, setupEdits: {} }); }}
        footer={null} width={680} centered bodyStyle={{ minHeight: "380px" }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: "24px" }}>
          {steps.map((s, i) => (
            <React.Fragment key={s}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
                <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: i < setupStep ? "#38A169" : i === setupStep ? NAVY : "#E2E8F0", color: i <= setupStep ? "white" : MUTED, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "13px", marginBottom: "4px", transition: "all .2s" }}>{i < setupStep ? "✓" : i + 1}</div>
                <div style={{ fontSize: "10px", fontWeight: i === setupStep ? 700 : 500, color: i === setupStep ? NAVY : MUTED, textAlign: "center", whiteSpace: "nowrap" }}>{s}</div>
              </div>
              {i < steps.length - 1 && <div style={{ flex: 2, height: "2px", marginBottom: "18px", background: i < setupStep ? "#38A169" : "#E2E8F0", transition: "background .2s" }} />}
            </React.Fragment>
          ))}
        </div>

        {setupStep === 0 && (
          <div>
            <div style={{ fontSize: "14px", color: MUTED, marginBottom: "16px", lineHeight: 1.6 }}>How would you like to set up your home profile? You can attach a report you've already run on the AI site, or start fresh by entering your address.</div>
            {(JSON.parse(localStorage.getItem("userDetails") || "{}").userProfile === "AG" || JSON.parse(localStorage.getItem("userDetails") || "{}").userProfile === "L") && (
              <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
                {["self", "client"].map(opt => (
                  <button key={opt} onClick={() => {
                    if (opt === "client") {
                      // Require Advanced+ to set up portals for clients
                      const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
                      const planRank = { free:0, basic:1, advanced:2, professional:3, team_leader:4 };
                      const rank = planRank[ud.planTier || "free"] || 0;
                      if (rank < 2) {
                        // Show upgrade notification inline — don't navigate away
                        notification.warning({
                          message: "Advanced Plan Required",
                          description: "Setting up client portals requires the Advanced plan. Visit your Subscriptions tab to upgrade.",
                          duration: 6,
                        });
                        return;
                      }
                    }
                    this.setState({ setupFor: opt });
                  }}
                    style={{ flex: 1, padding: "10px", borderRadius: "10px", cursor: "pointer", fontWeight: 700, fontSize: "13px", border: "2px solid", borderColor: this.state.setupFor === opt ? NAVY : BORDER, background: this.state.setupFor === opt ? `${NAVY}10` : WHITE, color: this.state.setupFor === opt ? NAVY : MUTED, transition: "all .15s" }}>
                    {opt === "self" ? "🏠 For My Home" : "👤 For a Client"}
                  </button>
                ))}
              </div>
            )}
            {this.state.setupFor === "client" && (
              <div style={{ background: `${NAVY}08`, borderRadius: "10px", padding: "14px", marginBottom: "14px", border: `1px solid ${NAVY}20` }}>
                <div style={{ fontSize: "12px", fontWeight: 700, color: NAVY, marginBottom: "10px" }}>
                  Client Details — their portal is permanently branded to you
                </div>

                {/* Existing client picker */}
                <div style={{ fontSize: "11px", fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "6px" }}>
                  Select Existing Client
                </div>
                <Select
                  showSearch allowClear
                  placeholder={this.state.myClients.length > 0 ? `Search ${this.state.myClients.length} clients...` : "No clients yet — enter below"}
                  style={{ width: "100%", marginBottom: "10px" }}
                  filterOption={(input, option) => (option.props.children || "").toLowerCase().includes(input.toLowerCase())}
                  onChange={v => {
                    if (!v) { this.setState({ clientName: "", clientEmail: "" }); return; }
                    const client = this.state.myClients.find(c => c.id === v);
                    if (client) this.setState({
                      clientName:  (`${client.firstName || ""} ${client.lastName || ""}`).trim(),
                      clientEmail: client.email || "",
                    });
                  }}>
                  {this.state.myClients.map(c => (
                    <Select.Option key={c.id} value={c.id}>
                      {(`${c.firstName || ""} ${c.lastName || ""}`).trim() + (c.email ? " — " + c.email : "")}
                    </Select.Option>
                  ))}
                </Select>

                <div style={{ textAlign: "center", fontSize: "11px", color: MUTED, marginBottom: "8px" }}>— or enter a new client —</div>

                <Input placeholder="Client name"
                  value={this.state.clientName}
                  onChange={e => this.setState({ clientName: e.target.value })}
                  style={{ marginBottom: "8px", borderRadius: "8px" }} />
                <Input placeholder="Client email address"
                  value={this.state.clientEmail}
                  onChange={e => this.setState({ clientEmail: e.target.value })}
                  style={{ marginBottom: this.state.clientEmail ? "10px" : "0", borderRadius: "8px" }} />

                {/* Client address pre-fill */}
                <Input placeholder="Property address (optional — can add later)"
                  value={this.state.setupAddress}
                  onChange={e => this.setState({ setupAddress: e.target.value })}
                  style={{ marginTop: "8px", borderRadius: "8px" }} />

                {this.state.clientEmail && (
                  <div style={{ background: `${GOLD}18`, borderRadius: "8px", padding: "9px 12px", border: `1px solid ${GOLD}35`, marginTop: "10px" }}>
                    <span style={{ fontSize: "11px", color: "#744210", fontWeight: 600 }}>
                      Just want to invite without a property?&nbsp;
                    </span>
                    <button onClick={() => {
                      this.setState({ inviteName: this.state.clientName, inviteEmail: this.state.clientEmail });
                      if (this.props.onCloseSetupModal) this.props.onCloseSetupModal();
                      setTimeout(() => this.setState({ showInviteModal: true }), 100);
                    }} style={{ background: "none", border: "none", color: NAVY, fontSize: "11px", fontWeight: 800, cursor: "pointer", textDecoration: "underline", padding: 0 }}>
                      Send invite only →
                    </button>
                  </div>
                )}
              </div>
            )}
            {savedReports.length === 0 && (
              <div style={{ background: "#FFF8E6", borderRadius: "10px", padding: "14px 16px", marginBottom: "12px", border: "1px solid #F6D860", fontSize: "13px", color: "#7A5010", lineHeight: 1.5 }}>
                <strong>No saved AI reports yet.</strong> After running a Home Health Analysis on the AI site, your report will appear here.{" "}
                <a href="https://ai.myhomebuddha.com" target="_blank" rel="noopener noreferrer" style={{ color: "#1A2280", fontWeight: 700 }}>Go to AI site →</a>
              </div>
            )}
            {savedReports.length > 0 && (
              <Card hoverable onClick={() => this.setState({ setupMode: "saved", setupStep: 1 })}
                style={{ marginBottom: "12px", borderRadius: "12px", cursor: "pointer", border: `2px solid ${setupMode === "saved" ? NAVY : BORDER}`, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", transition: "all .2s" }} bodyStyle={{ padding: "18px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div style={{ fontSize: "32px" }}>📋</div>
                  <div style={{ flex: 1 }}><div style={{ fontWeight: 700, color: NAVY, fontSize: "14px" }}>Use a Saved AI Report</div><div style={{ fontSize: "12px", color: MUTED, marginTop: "2px" }}>Attach one of your {savedReports.length} saved report{savedReports.length !== 1 ? "s" : ""} from the AI site</div></div>
                  <div style={{ fontSize: "20px", color: MUTED }}>→</div>
                </div>
              </Card>
            )}
            <Card hoverable onClick={() => this.setState({ setupMode: "address", setupStep: 1 })}
              style={{ borderRadius: "12px", cursor: "pointer", border: `2px solid ${setupMode === "address" ? NAVY : BORDER}`, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", transition: "all .2s" }} bodyStyle={{ padding: "18px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ fontSize: "32px" }}>📍</div>
                <div style={{ flex: 1 }}><div style={{ fontWeight: 700, color: NAVY, fontSize: "14px" }}>Enter My Address</div><div style={{ fontSize: "12px", color: MUTED, marginTop: "2px" }}>Pull public records for your home and build a profile from scratch</div></div>
                <div style={{ fontSize: "20px", color: MUTED }}>→</div>
              </div>
            </Card>
          </div>
        )}

        {setupStep === 1 && setupMode === "saved" && (
          <div>
            <div style={{ fontWeight: 700, color: NAVY, marginBottom: "12px" }}>Select a Saved Report</div>
            {savedReports.map(r => (
              <Card key={r.id} hoverable
                onClick={() => {
                  const rd = r.reportData || {};
                  const ha = rd.homeProfileAddress || rd.homeDetails || r.homeDetails || {};
                  const rmSubj = (rd.rmData && rd.rmData.subject) ? rd.rmData.subject : {};
                  const publicData = {
                    address: r.address || "",
                    yearBuilt:      String(ha.yearBuilt || rmSubj.yearBuilt || ha.year_built || ""),
                    squareFeet:     String(ha.sqft || ha.squareFootage || ha.livingArea || rmSubj.sqft || ""),
                    bedrooms:       String(ha.beds || ha.bedrooms || rmSubj.beds || ""),
                    bathrooms:      String(ha.baths || ha.bathrooms || rmSubj.baths || ""),
                    lotSize:        String(ha.lotSize || ha.lot_size || rmSubj.lotSize || ""),
                    propertyType:   String(ha.propertyType || ha.type || rmSubj.propertyType || ""),
                    stories:        String(ha.stories || ""),
                    garageSpaces:   String(ha.garageSpaces || ha.garage || ""),
                    heatingType:    String(ha.heating || ha.heatingType || ""),
                    coolingType:    String(ha.cooling || ha.coolingType || ""),
                    roofType:       String(ha.roofType || ha.roof || ""),
                    foundationType: String(ha.foundationType || ha.foundation || ""),
                    lastSoldYear:   ha.lastSaleDate ? new Date(ha.lastSaleDate).getFullYear().toString() : "",
                    estimatedValue: rd.rmData && rd.rmData.avm ? String(typeof rd.rmData.avm === "object" ? rd.rmData.avm.value || "" : rd.rmData.avm) : String(ha.estimatedValue || ha.price || ""),
                    county: String(ha.county || ""), state: String(ha.state || ha.stateCode || ""), zipCode: String(ha.zipCode || ha.zip || ha.postalCode || ""),
                  };
                  // After selecting a report, go to address step so agent can confirm/enter address and pull public records
                  this.setState({ selectedReport: r, setupStep: 1, setupMode: "address", setupAddress: r.address || r.addr || "" });
                }}
                style={{ marginBottom: "10px", borderRadius: "10px", border: `2px solid ${selectedReport?.id === r.id ? NAVY : BORDER}`, cursor: "pointer" }} bodyStyle={{ padding: "14px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ fontWeight: 600, color: NAVY, fontSize: "13px" }}>📍 {r.address}</div>
                  {(r.reportType || r.recordType) && <span style={{ fontSize: "10px", fontWeight: 700, padding: "2px 8px", borderRadius: "50px", background: `${NAVY}15`, color: NAVY, textTransform: "capitalize", flexShrink: 0, marginLeft: "8px" }}>{(r.reportType || r.recordType || "").replace(/_/g, " ")}</span>}
                </div>
                {r.updatedAt && <div style={{ fontSize: "11px", color: MUTED, marginTop: "3px" }}>Last updated {new Date(r.updatedAt).toLocaleDateString()}</div>}
              </Card>
            ))}
            <Button onClick={() => this.setState({ setupStep: 0 })} style={{ marginTop: "8px" }}>← Back</Button>
          </div>
        )}

        {setupStep === 1 && setupMode === "address" && (
          <div>
            <div style={{ fontWeight: 700, color: NAVY, marginBottom: "8px" }}>Enter Your Home Address</div>
            <Input.Search value={setupAddress} onChange={e => this.setState({ setupAddress: e.target.value })} placeholder="123 Main St, City, State ZIP" enterButton="Look Up Records" size="large" loading={setupLoading}
              onSearch={addr => { if (!addr.trim()) { notification.error({ message: "Please enter an address" }); return; } this.fetchPublicRecords(addr); }} style={{ marginBottom: "8px" }} />
            <div style={{ fontSize: "11px", color: MUTED, lineHeight: 1.5 }}>We'll pull publicly available property records and let you review and correct them before saving.</div>
            <Button onClick={() => this.setState({ setupStep: 0 })} style={{ marginTop: "16px" }}>← Back</Button>
          </div>
        )}

        {setupStep === 2 && (
          <div>
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontWeight: 700, color: NAVY, fontSize: "14px", marginBottom: "4px" }}>📍 {merged.address || setupAddress || selectedReport?.address}</div>
              <div style={{ fontSize: "12px", color: MUTED }}>Review the information below and correct anything that's wrong. These details power your AI home analysis.</div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "16px" }}>
              {FIELDS.map(({ key, label }) => (
                <div key={key}>
                  <div style={{ fontSize: "11px", fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "3px" }}>{label}</div>
                  <Input value={setupEdits[key] !== undefined ? setupEdits[key] : (merged[key] || "")} onChange={e => this.setState(s => ({ setupEdits: { ...s.setupEdits, [key]: e.target.value } }))} placeholder={"Enter " + label.toLowerCase()} size="small" style={{ borderRadius: "6px" }} />
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <Button onClick={() => this.setState({ setupStep: 1 })}>← Back</Button>
              <Button type="primary" style={{ background: NAVY, border: "none", borderRadius: "50px", fontWeight: 700, flex: 1 }} onClick={() => this.setState({ setupStep: 3 })}>Looks Good — Continue →</Button>
            </div>
          </div>
        )}

        {setupStep === 3 && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: "40px", marginBottom: "12px" }}>🤖</div>
            <div style={{ fontWeight: 800, color: NAVY, fontSize: "18px", marginBottom: "8px" }}>Ready to Build Your Home Profile</div>
            <div style={{ fontSize: "13px", color: MUTED, marginBottom: "24px", lineHeight: 1.6, maxWidth: "400px", margin: "0 auto 24px" }}>We'll save your home details and open your Home Buddha portal. Use the AI chat in each section to get personalized analysis and advice for your specific home.</div>
            <div style={{ background: LIGHT, borderRadius: "10px", padding: "14px 16px", marginBottom: "20px", textAlign: "left" }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: GOLD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "8px" }}>Your Home Summary</div>
              {[["Address", merged.address || setupAddress || selectedReport?.address], ["Year Built", merged.yearBuilt], ["Size", merged.squareFeet ? merged.squareFeet + " sq ft" : null], ["Beds / Baths", merged.bedrooms && merged.bathrooms ? `${merged.bedrooms} bed / ${merged.bathrooms} bath` : null], ["Type", merged.propertyType]].filter(([, v]) => v).map(([label, value]) => (
                <div key={label} style={{ display: "flex", gap: "8px", fontSize: "12px", marginBottom: "4px" }}>
                  <span style={{ color: MUTED, minWidth: "80px" }}>{label}:</span>
                  <span style={{ color: "#1A202C", fontWeight: 600 }}>{value}</span>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
              <Button onClick={() => this.setState({ setupStep: 2 })}>← Edit Details</Button>
              <Button type="primary" loading={setupLoading} onClick={this.saveAndAnalyze} style={{ background: GOLD, border: "none", borderRadius: "50px", fontWeight: 800, fontSize: "14px", height: "42px", padding: "0 28px", color: "#2A1600" }}>
                🚀 Save & Open My Home Portal
              </Button>
            </div>
          </div>
        )}
      </Modal>
    );
  }

  render() {
    const { loading, activeKey } = this.state;
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: "60px 20px", background: WHITE }}>
          <Spin size="large" />
          <div style={{ color: MUTED, marginTop: "16px", fontSize: "13px" }}>Loading your home portal...</div>
        </div>
      );
    }
    return (
      <div style={{ background: LIGHT, minHeight: "600px" }}>
        {/* ── Agent action buttons — FIRST, before homes list ────────── */}
        {(JSON.parse(localStorage.getItem("userDetails") || "{}").userProfile === "AG" ||
          JSON.parse(localStorage.getItem("userDetails") || "{}").userProfile === "L") && (
          <div style={{ display: "flex", gap: "10px", padding: "14px 16px 10px", flexWrap: "wrap" }}>
            <Button type="primary" icon={<PlusOutlined />}
              onClick={() => { if (this.props.onShowSetupModal) this.props.onShowSetupModal(); }}
              style={{ borderRadius: "50px", background: NAVY, border: "none", fontWeight: 700, flex: 1 }}>
              🏠 Create a Home Profile
            </Button>
            <Button
              onClick={() => this.setState({ showInviteModal: true })}
              style={{ borderRadius: "50px", background: GOLD, border: "none", color: "#2A1600", fontWeight: 700, flex: 1 }}>
              📨 Invite a Homeowner
            </Button>
          </div>
        )}

        {/* ── My Home Portals section — below action buttons ──────────── */}
        {this.state.homes.length > 0 && (
          <div style={{ padding: "4px 16px 0" }}>
            <div style={{ fontSize: "13px", fontWeight: 800, color: NAVY, marginBottom: "8px", textTransform: "uppercase", letterSpacing: ".04em" }}>
              🏡 My Home Portals
            </div>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
            {this.state.homes.map(h => (
              <button key={h.id} onClick={() => this.setState({ activeHomeId: h.id, aiReport: h })}
                style={{ padding: "5px 12px", borderRadius: "50px", cursor: "pointer", fontWeight: 600, fontSize: "12px", border: "2px solid", borderColor: this.state.activeHomeId === h.id ? NAVY : BORDER, background: this.state.activeHomeId === h.id ? NAVY : WHITE, color: this.state.activeHomeId === h.id ? WHITE : "#1A202C", transition: "all .15s" }}>
                🏡 {h.nickname || (h.address || "Home").split(",")[0]}
              </button>
            ))}
          </div>
          </div>
        )}

        <Tabs activeKey={activeKey} onChange={key => this.setState({ activeKey: key })} tabBarStyle={{ padding: "0 16px", background: WHITE, marginBottom: 0 }}
          tabBarExtraContent={<Button size="small" icon={<ReloadOutlined />} onClick={this.loadAll} style={{ borderRadius: "50px", color: MUTED, border: `1px solid ${BORDER}`, marginRight: "8px" }}>Refresh</Button>}>
          <TabPane tab={<span>🏠 Overview</span>} key="overview">
            <div style={{ padding: "16px" }}>{this.renderOverview()}</div>
          </TabPane>
          {SECTIONS.map(section => (
            <TabPane key={section.key} tab={
              <span>
                {section.emoji} {section.label.split(" ")[0]}
                {this.getSectionScore(section.key) && (
                  <span style={{ marginLeft: "4px", fontSize: "10px", background: `${section.color}20`, color: section.color, borderRadius: "50px", padding: "1px 6px" }}>
                    {this.getAIDataForSection(section.key) ? "AI" : ""}
                    {(this.state.manualEntries[section.key] || []).length > 0 ? `${this.getAIDataForSection(section.key) ? "+" : ""}${this.state.manualEntries[section.key].length}` : ""}
                  </span>
                )}
              </span>
            }>
              <div style={{ padding: "16px" }}>{this.renderSection(section)}</div>
            </TabPane>
          ))}
        </Tabs>
        <style>{`@keyframes pulse { 0%, 100% { opacity: 0.3; transform: scale(0.8); } 50% { opacity: 1; transform: scale(1); } }`}</style>
        {this.renderSetupModal()}
        {this.renderAgentChatPanel()}
        {this.renderInviteModal()}
        {this.renderHelpModal()}
      </div>
    );
  }
}

export default HomeBuddha;